import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { hasPageSeoOverride } from './utils/seo'

export const languages = [
  { code: 'tr', label: 'Türkçe', shortLabel: 'TR', locale: 'tr-TR' },
  { code: 'en', label: 'English', shortLabel: 'EN', locale: 'en-US' },
  { code: 'de', label: 'Deutsch', shortLabel: 'DE', locale: 'de-DE' },
  { code: 'fr', label: 'Français', shortLabel: 'FR', locale: 'fr-FR' },
] as const

export type Language = typeof languages[number]['code']

const SEO_META: Record<Language, { title: string; description: string; ogLocale: string }> = {
  tr: {
    title: 'dotkesk — WhatsApp, Instagram ve Telegram için AI Agent | Omnichannel CRM',
    description: 'dotkesk; WhatsApp, Instagram ve Telegram\'dan gelen müşteri mesajlarını yapay zekâ ajanınızla anında yanıtlar, doğru ürünü bulup link gönderir ve tüm konuşmaları tek ekranda toplar. Ücretsiz demo talep edin.',
    ogLocale: 'tr_TR',
  },
  en: {
    title: 'dotkesk — AI Agent for WhatsApp, Instagram and Telegram | Omnichannel CRM',
    description: 'dotkesk replies to your WhatsApp, Instagram and Telegram messages instantly with an AI agent, finds the right product and sends the link, and brings every conversation into one screen. Request a free demo.',
    ogLocale: 'en_US',
  },
  de: {
    title: 'dotkesk — KI-Agent für WhatsApp, Instagram und Telegram | Omnichannel-CRM',
    description: 'dotkesk beantwortet Ihre WhatsApp-, Instagram- und Telegram-Nachrichten sofort mit einem KI-Agenten, findet das richtige Produkt und sendet den Link. Fordern Sie eine kostenlose Demo an.',
    ogLocale: 'de_DE',
  },
  fr: {
    title: 'dotkesk — Agent IA pour WhatsApp, Instagram et Telegram | CRM omnicanal',
    description: 'dotkesk répond instantanément à vos messages WhatsApp, Instagram et Telegram avec un agent IA, trouve le bon produit et envoie le lien. Demandez une démo gratuite.',
    ogLocale: 'fr_FR',
  },
}

interface PublicI18nContextValue {
  language: Language
  locale: string
  setLanguage: (language: Language) => void
}

const PublicI18nContext = createContext<PublicI18nContextValue | null>(null)
const LANGUAGE_STORAGE_KEY = 'crm-language-v2'

const isLanguage = (value: string | null): value is Language =>
  languages.some((language) => language.code === value)

const updateMetaTag = (selector: string, content: string) => {
  const element = document.querySelector<HTMLMetaElement>(selector)
  if (element) element.content = content
}

export function PublicI18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY)
    return isLanguage(saved) ? saved : 'en'
  })

  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
    document.documentElement.lang = language
    if (hasPageSeoOverride()) return

    const meta = SEO_META[language]
    document.title = meta.title
    updateMetaTag('meta[name="description"]', meta.description)
    updateMetaTag('meta[property="og:title"]', meta.title)
    updateMetaTag('meta[property="og:description"]', meta.description)
    updateMetaTag('meta[property="og:locale"]', meta.ogLocale)
    updateMetaTag('meta[name="twitter:title"]', meta.title)
    updateMetaTag('meta[name="twitter:description"]', meta.description)
  }, [language])

  const value = useMemo(() => ({
    language,
    locale: languages.find((item) => item.code === language)?.locale ?? 'tr-TR',
    setLanguage,
  }), [language])

  return <PublicI18nContext.Provider value={value}>{children}</PublicI18nContext.Provider>
}

export function usePublicI18n() {
  const context = useContext(PublicI18nContext)
  if (!context) throw new Error('usePublicI18n must be used inside PublicI18nProvider')
  return context
}
